import React from 'react';
import { useClient } from '../../context/ClientContext';

export default function MonthEndForecast({ summary, dateRange }) {
  const { brand, dataSources } = useClient();
  const currencySymbol = dataSources?.googleAds?.currencySymbol || brand?.currencySymbol || '$';

  if (!summary || summary.enquiries == null) return null;
  // Only meaningful for month-to-date data
  if (dateRange && dateRange !== 'mtd') return null;

  const now = new Date();
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  if (dayOfMonth < 3) return null; // Not enough data early in month

  const dailyEnquiryRate = summary.enquiries / dayOfMonth;
  const dailySpendRate = summary.cost / dayOfMonth;

  const projectedEnquiries = Math.round(dailyEnquiryRate * daysInMonth);
  const projectedSpend = dailySpendRate * daysInMonth;
  const projectedCPA = projectedEnquiries > 0 ? projectedSpend / projectedEnquiries : 0;

  const target = summary.target;
  const targetCPA = dataSources?.features?.targetCPA;

  // Status colours
  let enquiryStatus = 'text-gray-900';
  if (target) {
    if (projectedEnquiries >= target) enquiryStatus = 'text-emerald-600';
    else if (projectedEnquiries >= target * 0.8) enquiryStatus = 'text-amber-600';
    else enquiryStatus = 'text-red-600';
  }

  let cpaStatus = 'text-gray-900';
  if (targetCPA) {
    if (projectedCPA <= targetCPA) cpaStatus = 'text-emerald-600';
    else if (projectedCPA <= targetCPA * 1.2) cpaStatus = 'text-amber-600';
    else cpaStatus = 'text-red-600';
  }

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
              style={{ fontFamily: brand.typography?.headerFont }}>
            Month-End Forecast
          </h3>
          <p className="text-xs text-gray-400 mt-1">Based on current daily rate</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="text-center p-3 rounded-lg bg-gray-50">
          <p className={`text-2xl font-bold ${enquiryStatus}`}>
            {projectedEnquiries}
          </p>
          <p className="text-xs text-gray-500 mt-1">Projected enquiries</p>
          {target && (
            <p className="text-xs text-gray-400 mt-0.5">Target: {target}</p>
          )}
        </div>

        <div className="text-center p-3 rounded-lg bg-gray-50">
          <p className="text-2xl font-bold text-gray-900">
            {currencySymbol}{projectedSpend.toLocaleString('en-AU', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">Projected spend</p>
        </div>

        <div className="text-center p-3 rounded-lg bg-gray-50">
          <p className={`text-2xl font-bold ${cpaStatus}`}>
            {currencySymbol}{projectedCPA.toLocaleString('en-AU', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">Projected CPA</p>
          {targetCPA && (
            <p className="text-xs text-gray-400 mt-0.5">Target: {currencySymbol}{targetCPA}</p>
          )}
        </div>
      </div>
    </div>
  );
}
